import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button, Icon, Spinner } from '../ui/index.ts';
import styles from './shell.module.css';
import { failedChunkUrl, isChunkLoadError, reloadWhenServerAnswers, serverAnswers, whenOnline } from './staleBuild.ts';

export interface ErrorBoundaryProps {
  children?: ReactNode;
  /** When this value changes (e.g. the route), a shown error screen is cleared. */
  resetKey?: string;
  /** «На главную» — the shell passes a navigation callback; omitted for the outermost boundary. */
  onGoHome?: () => void;
  /** true = rendered inside the app frame (the mascot dock stays visible) */
  inline?: boolean;
}

interface ErrorBoundaryState {
  error: Error | null;
  resetKey: string | undefined;
  /** a chunk failed and the server is being asked whether it answers (a stale build reloads): a spinner, not the card */
  probing: boolean;
}

/** Friendly safety net: a crash of one screen never shows a white page to the child. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, resetKey: this.props.resetKey, probing: false };
  /** the wait for the window's 'online' event (a chunk failed offline) */
  private stopWaiting: (() => void) | null = null;
  private unmounted = false;

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    const e = error instanceof Error ? error : new Error(String(error));
    return { error: e, probing: isChunkLoadError(e) };
  }

  static getDerivedStateFromProps(props: ErrorBoundaryProps, state: ErrorBoundaryState): Partial<ErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return { error: null, resetKey: props.resetKey, probing: false };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('[shell] a screen crashed', error, info.componentStack);
    // a chunk of an older build (the page was opened before a deploy) — or of no build at all, offline (review round 3)
    if (isChunkLoadError(error)) void this.recover(error);
  }

  override componentWillUnmount(): void {
    this.unmounted = true;
    this.stopWaiting?.();
    this.stopWaiting = null;
  }

  /**
   * The new build comes with a reload — when the server answers. Offline the card stays (the shell and «На главную»
   * keep working; a reload would land on the browser's own error page) and the reload waits for the network.
   */
  private async recover(error: unknown, asked = false): Promise<void> {
    // the child's «Попробовать снова» reloads whenever the server answers (the automatic reload stays once a minute)
    const reloading = asked ? await serverAnswers(failedChunkUrl(error)) : await reloadWhenServerAnswers(error);
    if (asked && reloading) window.location.reload();
    if (reloading || this.unmounted) return;
    if (this.state.error !== null) this.setState({ probing: false });
    this.stopWaiting?.();
    this.stopWaiting = whenOnline(() => {
      this.stopWaiting = null;
      if (!this.unmounted && this.state.error !== null && isChunkLoadError(this.state.error)) void this.recover(error);
    });
  }

  private readonly retry = (): void => {
    // re-rendering cannot fetch a chunk that is gone — only a reload brings the new build, and only when the server
    // answers (offline the card stays)
    if (isChunkLoadError(this.state.error)) {
      this.setState({ probing: true });
      void this.recover(this.state.error, true);
      return;
    }
    this.setState({ error: null });
  };

  private readonly goHome = (): void => {
    this.props.onGoHome?.();
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error, probing } = this.state;
    if (error === null) return this.props.children;
    if (probing) {
      return (
        <div className={styles.error} data-inline={this.props.inline ? 'true' : 'false'} aria-busy="true">
          <Spinner size={72} showLabel />
        </div>
      );
    }

    return (
      <div className={styles.error} data-inline={this.props.inline ? 'true' : 'false'} role="alert">
        <div className={styles.errorCard}>
          <div className={styles.errorArt} aria-hidden="true">
            🧩
          </div>
          <h1>Ой! Что-то запуталось</h1>
          <p>Так бывает даже у гроссмейстеров. Твои партии в порядке — давай попробуем ещё раз.</p>
          <div className={styles.errorActions}>
            <Button size="lg" icon={<Icon name="refresh" />} onClick={this.retry}>
              Попробовать снова
            </Button>
            {this.props.onGoHome ? (
              <Button size="lg" variant="secondary" icon={<Icon name="home" />} onClick={this.goHome}>
                На главную
              </Button>
            ) : (
              <Button size="lg" variant="secondary" onClick={() => window.location.reload()}>
                Перезагрузить
              </Button>
            )}
          </div>
          <details className={styles.errorDetails}>
            <summary>Для родителей: что случилось</summary>
            <pre>{error.stack ?? error.message}</pre>
          </details>
        </div>
      </div>
    );
  }
}
